import { ArrowLeftIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';
import { Link, useParams } from 'react-router-dom';

import { Card, Badge, Button, Spinner } from '@realvisor/ui';
import { useProject } from '@/hooks';

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('cs-CZ', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch (error) {
    console.error('Invalid date format:', dateString, error);
    return '-';
  }
}

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const { data: project, isLoading, error } = useProject(id || '');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <Card padding="lg">
        <div className="text-center py-8">
          <p className="text-red-500">Projekt nebyl nalezen</p>
          <Link to="/projects" className="mt-4 inline-block text-primary-600 hover:underline">
            Zpět na seznam
          </Link>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/projects"
          className="flex items-center justify-center rounded-lg border border-gray-200 p-2 hover:bg-gray-50"
        >
          <ArrowLeftIcon className="h-5 w-5 text-gray-500" />
        </Link>
        {project.logoUrl && project.logoUrl.startsWith('http') ? (
          <img 
            src={project.logoUrl} 
            alt={project.name}
            className="h-10 w-10 rounded-lg object-contain"
          />
        ) : (
          <div 
            className="h-10 w-10 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: project.color || '#3B82F6' }}
          >
            <span className="text-white font-bold text-lg">
              {project.name.charAt(0).toUpperCase()}
            </span>
          </div>
        )}
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{project.name}</h1>
          <p className="text-sm text-gray-500">{project.slug}</p>
        </div>
        <Badge variant={project.archivedAt ? 'default' : 'success'}>
          {project.archivedAt ? 'Archivován' : 'Aktivní'}
        </Badge>
        <Link to={`/projects/${project.id}/settings`}>
          <Button variant="outline">
            <Cog6ToothIcon className="mr-2 h-4 w-4" />
            Nastavení
          </Button>
        </Link>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <div className="p-4">
            <p className="text-sm text-gray-500">Celkem leadů</p>
            <p className="text-2xl font-bold text-gray-900">{project._count?.leads || 0}</p>
          </div>
        </Card>
        <Card>
          <div className="p-4">
            <p className="text-sm text-gray-500">Vytvořeno</p>
            <p className="text-lg font-medium text-gray-900">{formatDate(project.createdAt)}</p>
          </div>
        </Card>
        <Card>
          <div className="p-4">
            <p className="text-sm text-gray-500">Pipeline</p>
            <p className="text-lg font-medium text-gray-900">
              {project.defaultPipeline?.name || 'Výchozí'}
            </p>
          </div>
        </Card>
      </div>

      {/* Content */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Základní informace</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                {/* Logo */}
                <div className="flex-shrink-0">
                  {project.logoUrl && project.logoUrl.startsWith('http') ? (
                    <img 
                      src={project.logoUrl} 
                      alt={project.name}
                      className="h-16 w-16 rounded-lg object-contain border border-gray-200"
                    />
                  ) : (
                    <div 
                      className="h-16 w-16 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: project.color || '#3B82F6' }}
                    >
                      <span className="text-white font-bold text-2xl">
                        {project.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-lg font-semibold text-gray-900">{project.name}</p>
                  <p className="text-sm text-gray-500">{project.slug}</p>
                  {project.description && (
                    <p className="mt-1 text-sm text-gray-600">{project.description}</p>
                  )}
                </div>
              </div>
              
              <div className="border-t pt-4 grid grid-cols-2 gap-4">
                <div>
                  <span className="text-xs text-gray-500 uppercase tracking-wide">Pipeline</span>
                  <p className="font-medium text-gray-900">{project.defaultPipeline?.name || 'Není nastavena'}</p>
                </div>
                <div>
                  <span className="text-xs text-gray-500 uppercase tracking-wide">Barva</span>
                  <div className="flex items-center gap-2 mt-0.5">
                    <div 
                      className="h-5 w-5 rounded-full border border-gray-200" 
                      style={{ backgroundColor: project.color || '#3B82F6' }}
                    />
                    <span className="font-medium text-gray-600 text-sm">{project.color || '#3B82F6'}</span>
                  </div>
                </div>
                <div>
                  <span className="text-xs text-gray-500 uppercase tracking-wide">Vytvořeno</span>
                  <p className="font-medium text-gray-900">{formatDate(project.createdAt)}</p>
                </div>
                <div>
                  <span className="text-xs text-gray-500 uppercase tracking-wide">Stav</span>
                  <p className="font-medium text-gray-900">{project.archivedAt ? 'Archivován' : 'Aktivní'}</p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Leady</h2>
            {project._count?.leads === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>Zatím žádné leady</p>
                <Link to="/leads" className="mt-2 inline-block text-primary-600 hover:underline">
                  Zobrazit všechny leady →
                </Link>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-3xl font-bold text-gray-900">{project._count?.leads}</p>
                <p className="text-gray-500">leadů v tomto projektu</p>
                <Link to="/leads" className="mt-4 inline-block text-primary-600 hover:underline">
                  Zobrazit leady →
                </Link>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
